import { format } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import type { TimeEntryType } from '@/types/timesheet';
import type { UpcomingAppointment } from '@/hooks/technicians/useTechnicianInsights';

export const TIME_ENTRY_TYPES: TimeEntryType[] = ['REGULAR', 'OVERTIME', 'TRAVEL'];

// Time entries belong to the technician's local day (the backend buckets pay
// periods that way). The office user may be in a different timezone, so every
// day/time conversion is done in the technician's timezone, never the viewer's.
// Falls back to the backend default rather than browser-local when unknown.
const DEFAULT_TZ = 'America/New_York';

/** Render an instant's wall-clock time (HH:mm) in the technician's timezone. */
export function timeOf(iso: string | null, tz: string = DEFAULT_TZ): string {
  return iso ? format(new TZDate(new Date(iso).getTime(), tz), 'HH:mm') : '';
}

/** An instant as a readable clock time ("7:42 AM") in the technician's timezone. */
export function clockTimeLabel(iso: string, tz: string = DEFAULT_TZ): string {
  return format(new TZDate(new Date(iso).getTime(), tz), 'h:mm a');
}

/** Short zone name for `tz` at `at` — "CDT", "GMT+1". */
export function timeZoneAbbreviation(tz: string = DEFAULT_TZ, at: Date = new Date()): string {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? tz;
}

/** Render an instant's local day (yyyy-MM-dd) in the technician's timezone. */
export function dateOf(iso: string, tz: string = DEFAULT_TZ): string {
  return format(new TZDate(new Date(iso).getTime(), tz), 'yyyy-MM-dd');
}

/**
 * Combine a YYYY-MM-DD day with an HH:mm time — interpreted as wall-clock time
 * in the technician's timezone `tz` — into a UTC ISO string.
 */
export function isoFrom(dateKey: string, time: string, tz: string = DEFAULT_TZ): string | null {
  if (!time) return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  if ([year, month, day, hours, minutes].some((n) => Number.isNaN(n))) {
    return null;
  }
  // TZDate.toISOString() keeps the zone offset; normalize to a UTC "Z" string.
  const instant = TZDate.tz(tz, year, month - 1, day, hours, minutes).getTime();
  return new Date(instant).toISOString();
}

export function typeLabel(t: string): string {
  return t.charAt(0) + t.slice(1).toLowerCase();
}

/** The local day after `dateKey` (yyyy-MM-dd), rollover-safe. */
function nextDayKey(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
}

/**
 * Resolve the clockOut ISO for a shift starting at `start` on `dateKey`. When
 * the end time is earlier than the start time the shift is treated as overnight
 * and clockOut advances to the next local day. Returns null when `end` is empty.
 */
export function resolveClockOut(
  dateKey: string,
  start: string,
  end: string,
  tz: string = DEFAULT_TZ,
): string | null {
  if (!end) return null;
  const clockIn = isoFrom(dateKey, start, tz);
  const sameDayOut = isoFrom(dateKey, end, tz);
  if (!clockIn || !sameDayOut) return null;
  return new Date(sameDayOut).getTime() < new Date(clockIn).getTime()
    ? isoFrom(nextDayKey(dateKey), end, tz)
    : sameDayOut;
}

/** An appointment's display pieces, dated in the technician's timezone. */
export function appointmentParts(
  a: UpcomingAppointment,
  tz: string = DEFAULT_TZ,
): { date: string | null; job: string; customer: string | null } {
  return {
    date: a.startAt ? format(new TZDate(new Date(a.startAt).getTime(), tz), 'EEE, MMM d') : null,
    job: a.jobName ?? a.title,
    customer: a.customerName || null,
  };
}

/** Option label for a technician appointment: date · job · customer. */
export function appointmentOptionLabel(a: UpcomingAppointment, tz: string = DEFAULT_TZ): string {
  const job = a.jobName ?? a.title;
  return [
    a.startAt ? format(new TZDate(new Date(a.startAt).getTime(), tz), 'MMM d') : null,
    job,
    a.customerName,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The technician-local midnight of `dayKey` as a UTC epoch (ms). */
function localDayStartMs(dayKey: string, tz: string): number {
  const [year, month, day] = dayKey.split('-').map(Number);
  return TZDate.tz(tz, year, month - 1, day, 0, 0).getTime();
}

/**
 * Sort a technician's appointments for the picker: closest to the worked day
 * first, then most recent. Proximity is measured against the technician's local
 * day. Appointments without a startAt go last.
 */
export function sortAppointmentsForDay(
  appointments: UpcomingAppointment[],
  dayKey: string,
  tz: string = DEFAULT_TZ,
): UpcomingAppointment[] {
  const dayMs = localDayStartMs(dayKey, tz);
  return [...appointments].sort((a, b) => {
    const aMs = a.startAt ? new Date(a.startAt).getTime() : null;
    const bMs = b.startAt ? new Date(b.startAt).getTime() : null;
    if (aMs == null && bMs == null) return 0;
    if (aMs == null) return 1;
    if (bMs == null) return -1;
    const distDiff = Math.abs(aMs - dayMs) - Math.abs(bMs - dayMs);
    if (distDiff !== 0) return distDiff;
    return bMs - aMs;
  });
}
